import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const StatusDot = { active: "#10B981", paused: "#F5A623", closed: "#71717A" };

export default function Searches() {
  const meta = useMeta();
  const navigate = useNavigate();
  const [searches, setSearches] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    organization_id: "", title: "", role_type: "executive", hiring_type: "engaged",
    location: "", compensation_range: "", search_brief: "", description: "",
  });

  const load = () => api.get("/searches").then(({ data }) => setSearches(data));
  useEffect(() => {
    load();
    api.get("/organizations").then(({ data }) => setOrgs(data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.organization_id) return toast.error("Select an organization");
    setSaving(true);
    try {
      const { data } = await api.post("/searches", form);
      toast.success("Search created");
      setOpen(false);
      navigate(`/app/searches/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader overline="Mandates" title="Searches">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-search-btn" className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white transition-colors">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create Search</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Organization</Label>
                <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                  <SelectTrigger data-testid="search-org-select" className="bg-white border-black/10 rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="bg-[#F4F4F5] border-black/10">
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {orgs.length === 0 && <p className="text-xs text-muted-foreground">Create an organization first.</p>}
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input data-testid="search-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-white border-black/10 rounded-none" placeholder="VP of Engineering" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Role type</Label>
                  <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })}>
                    <SelectTrigger className="bg-white border-black/10 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#F4F4F5] border-black/10">
                      {meta.role_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hiring type</Label>
                  <Select value={form.hiring_type} onValueChange={(v) => setForm({ ...form, hiring_type: v })}>
                    <SelectTrigger className="bg-white border-black/10 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#F4F4F5] border-black/10">
                      {meta.hiring_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white border-black/10 rounded-none" placeholder="Remote / SF" />
                </div>
                <div className="space-y-2">
                  <Label>Compensation</Label>
                  <Input value={form.compensation_range} onChange={(e) => setForm({ ...form, compensation_range: e.target.value })} className="bg-white border-black/10 rounded-none" placeholder="$250k–$320k" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Search brief</Label>
                <Textarea value={form.search_brief} onChange={(e) => setForm({ ...form, search_brief: e.target.value })} className="bg-white border-black/10 rounded-none min-h-[80px]" placeholder="Ideal profile, must-haves, context..." />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="search-save-btn" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Search"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {!searches && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {searches && searches.length === 0 && <Empty icon={Search} title="No searches yet" hint="Create your first search mandate to build a pipeline." />}
        {searches && searches.length > 0 && (
          <div className="border border-black/10">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-black/10 overline text-[0.55rem] bg-white">
              <span className="col-span-5">Search</span>
              <span className="col-span-3">Organization</span>
              <span className="col-span-2">Role</span>
              <span className="col-span-1">Pipeline</span>
              <span className="col-span-1">Status</span>
            </div>
            {searches.map((s) => (
              <button
                key={s.id}
                data-testid={`search-row-${s.id}`}
                onClick={() => navigate(`/app/searches/${s.id}`)}
                className="w-full grid grid-cols-12 px-5 py-4 border-b border-black/5 hover:bg-black/5 transition-colors text-left items-center"
              >
                <span className="col-span-5 font-medium truncate pr-4">{s.title}</span>
                <span className="col-span-3 text-sm text-muted-foreground truncate pr-4">{s.organization_name}</span>
                <span className="col-span-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">{s.role_type}</span>
                <span className="col-span-1 font-heading font-bold">{s.candidate_count}</span>
                <span className="col-span-1 flex items-center gap-1.5 text-xs capitalize">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: StatusDot[s.status] || "#71717A" }} />
                  {s.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
